/**
 * What this document last saw of each question, kept in the document's own
 * settings so it travels with the file (an emailed copy knows what it was
 * based on). No server-side record of documents or copies exists: the push
 * sheet compares this hash with mytender's current answer to say "Newer in
 * mytender", and the last push time drives the "last pushed 6 days ago" note.
 */

export const SYNC_SETTING = "mytender_sync";
const SAVE_TIMEOUT_MS = 5000;

/**
 * `hash` is mytender's answer as it was then; `docHash` is this document's
 * answer as it was then, read back from Word. The two differ in form (Word
 * and mytender write lists, links and escapes differently), so each side is
 * only ever compared with itself.
 */
export type QuestionSync = { hash: string; docHash?: string; at: string; direction: "pull" | "push" };
export type SyncRecord = { bidId: string; lastPushAt: string | null; questions: Record<string, QuestionSync> };

const available = (): boolean => {
  try {
    return Boolean(Office?.context?.document?.settings);
  } catch {
    return false;
  }
};

let memory: SyncRecord | null = null;

export const readSyncRecord = (bidId: string): SyncRecord => {
  const empty: SyncRecord = { bidId, lastPushAt: null, questions: {} };
  if (!available()) return memory?.bidId === bidId ? memory : empty;
  try {
    const raw = Office.context.document.settings.get(SYNC_SETTING) as string | null;
    const parsed = raw ? (JSON.parse(raw) as SyncRecord) : null;
    // A record for another bid (the document was relinked) means nothing here.
    return parsed && parsed.bidId === bidId ? parsed : empty;
  } catch {
    return empty;
  }
};

const save = (record: SyncRecord): Promise<void> => {
  if (!available()) {
    memory = record;
    return Promise.resolve();
  }
  Office.context.document.settings.set(SYNC_SETTING, JSON.stringify(record));
  // The value is set either way; saveAsync only persists it into the file.
  // A callback that never comes (seen on some hosts) must not hang a pull.
  return new Promise((resolve) => {
    const timer = setTimeout(resolve, SAVE_TIMEOUT_MS);
    Office.context.document.settings.saveAsync(() => {
      clearTimeout(timer);
      resolve();
    });
  });
};

/** Record what was just pulled or pushed for some questions. */
export const recordSync = async (
  bidId: string,
  entries: { sectionId: string; hash: string; docHash?: string }[],
  direction: "pull" | "push"
): Promise<void> => {
  if (!entries.length) return;
  const record = readSyncRecord(bidId);
  const at = new Date().toISOString();
  for (const e of entries)
    record.questions[e.sectionId] = { hash: e.hash, ...(e.docHash && { docHash: e.docHash }), at, direction };
  if (direction === "push") record.lastPushAt = at;
  await save(record);
};
